"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  ArrowRight,
  Store,
  ShoppingBag,
  Users,
  TrendingUp,
  Shield,
  Zap,
  Globe,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

interface CarouselMessage {
  id: string;
  title: string;
  subtitle: string;
  primaryButton: string;
  secondaryButton: string;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string;
}

const carouselMessages: CarouselMessage[] = [
  {
    id: "buyers",
    title: "Discover quality second-hand from trusted sellers",
    subtitle: "Find unique items with safe payments and reliable delivery",
    primaryButton: "Start Shopping",
    secondaryButton: "Browse Categories",
    icon: ShoppingBag,
    gradient: "from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950"
  },
  {
    id: "sellers-marketplace",
    title: "Join the marketplace and reach thousands of buyers",
    subtitle: "Professional selling tools, analytics, and dedicated support",
    primaryButton: "Start Selling",
    secondaryButton: "Seller Dashboard",
    icon: Store,
    gradient: "from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950"
  },
  {
    id: "sellers-whitelabel",
    title: "Your own branded website, powered by our technology",
    subtitle: "White-label solution with your domain, branding, and full control",
    primaryButton: "Get White Label",
    secondaryButton: "Learn More",
    icon: Globe,
    gradient: "from-purple-50 to-violet-50 dark:from-purple-950 dark:to-violet-950"
  }
];

interface HeroCarouselProps {
  onButtonClick?: (buttonType: 'primary' | 'secondary', messageId: string) => void;
}

export function HeroCarousel({ onButtonClick }: HeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');

  const nextSlide = () => {
    if (isTransitioning) return;
    setSlideDirection('right');
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselMessages.length);
      setIsTransitioning(false);
    }, 150);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setSlideDirection('left');
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + carouselMessages.length) % carouselMessages.length);
      setIsTransitioning(false);
    }, 150);
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    const direction = index > currentSlide ? 'right' : 'left';
    setSlideDirection(direction);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, 150);
  };

  const handleButtonClick = (buttonType: 'primary' | 'secondary') => {
    const currentMessage = carouselMessages[currentSlide];
    onButtonClick?.(buttonType, currentMessage.id);
  };

  const currentMessage = carouselMessages[currentSlide];

  return (
    <section className={`relative flex flex-col items-center justify-center text-center py-20 px-6 md:py-32 bg-gradient-to-b ${currentMessage.gradient} transition-all duration-700 ease-in-out overflow-hidden`}>
      <div className="max-w-7xl relative z-10">
        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          disabled={isTransitioning}
          className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all duration-300 z-20 ${
            isTransitioning ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
          }`}
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <button
          onClick={nextSlide}
          disabled={isTransitioning}
          className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all duration-300 z-20 ${
            isTransitioning ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
          }`}
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </button>

        {/* Animated content container */}
        <div className={`transition-all duration-700 ease-in-out ${
          isTransitioning
            ? `opacity-0 transform ${slideDirection === 'right' ? '-translate-x-8' : 'translate-x-8'}`
            : 'opacity-100 transform translate-x-0'
        }`}>
          <div className={`flex items-center justify-center mb-6 transition-all duration-700 ease-in-out ${
            isTransitioning
              ? `opacity-0 transform ${slideDirection === 'right' ? '-translate-x-4' : 'translate-x-4'}`
              : 'opacity-100 transform translate-x-0'
          }`}>
            <Badge variant="secondary" className="px-4 py-2 text-sm font-medium">
              {currentMessage.id === "buyers" ? "For Buyers" :
               currentMessage.id === "sellers-marketplace" ? "For Sellers" : "Enterprise"}
            </Badge>
          </div>

          <h1 className={`text-4xl md:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 transition-all duration-700 ease-in-out delay-100 ${
            isTransitioning
              ? `opacity-0 transform ${slideDirection === 'right' ? '-translate-x-6' : 'translate-x-6'}`
              : 'opacity-100 transform translate-x-0'
          }`}>
            {currentMessage.title}
          </h1>
          <p className={`text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto transition-all duration-700 ease-in-out delay-200 ${
            isTransitioning
              ? `opacity-0 transform ${slideDirection === 'right' ? '-translate-x-4' : 'translate-x-4'}`
              : 'opacity-100 transform translate-x-0'
          }`}>
            {currentMessage.subtitle}
          </p>

          <div className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 ease-in-out delay-300 ${
            isTransitioning
              ? `opacity-0 transform ${slideDirection === 'right' ? 'translate-x-4' : '-translate-x-4'}`
              : 'opacity-100 transform translate-x-0'
          }`}>
            <Button
              size="lg"
              className="px-8 py-3 text-base font-medium hover:scale-105 transition-transform"
              onClick={() => handleButtonClick('primary')}
            >
              {currentMessage.primaryButton} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="px-8 py-3 text-base font-medium hover:scale-105 transition-transform"
              onClick={() => handleButtonClick('secondary')}
            >
              {currentMessage.secondaryButton}
            </Button>
          </div>
        </div>

        {/* Enhanced slide indicators */}
        <div className="flex justify-center mt-12 space-x-3 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500">
          {carouselMessages.map((message, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              className={`relative transition-all duration-300 ${
                isTransitioning ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-125'
              }`}
            >
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? "bg-primary w-12 h-3"
                  : "bg-gray-300 hover:bg-gray-400"
              }`} />
              {index === currentSlide && (
                <div className="absolute inset-0 bg-primary/30 rounded-full animate-pulse" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-24 h-24 bg-white/10 rounded-full blur-lg animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/5 rounded-full blur-md animate-pulse delay-500" />
      </div>
    </section>
  );
}
